import { useState } from "react";
import { clientsApi, ordersApi, profileClientApi, productsApi, CreateOrderDto } from "@/lib/api";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { getUserEmailSync } from "@/lib/oidcAuth";
import {
  isCycleRequiredError,
  isInvalidCycleCodeFormatError,
  isCycleNotConfiguredError,
  isCycleNotOpenError,
} from "@/lib/cycleError";
import { buildCycleCode } from "@/lib/orderCycle";

const processingClients = new Map<string, boolean>();

/** Para ciclos novos, envia apenas intenção; o backend calcula a composição oficial. */
export const useScheduledOrderCreate = () => {
  const { user, selectedCycleYear } = useUser();
  const [isCreating, setIsCreating] = useState(false);

  const createScheduledOrder = async (clientCode: string) => {
    if (!clientCode) {
      toast.error("Código do cliente não fornecido.");
      return;
    }
    if (processingClients.get(clientCode)) return;

    setIsCreating(true);
    processingClients.set(clientCode, true);
    const toastId = toast.loading(`Gerando pedido programado para o cliente ${clientCode}...`);

    try {
      const clientResponse = await clientsApi.getByCode(clientCode);
      const selectedClient = clientResponse.data || clientResponse;
      if (!selectedClient) throw new Error("Cliente não encontrado.");
      if (!selectedCycleYear) throw new Error("Selecione o ciclo comercial antes de gerar o pedido programado.");

      const clientName =
        (selectedClient as any).nome_cliente ||
        (selectedClient as any).clientCompanyName ||
        clientCode;
      const orderNumber = `PED-S-${Date.now()}-${clientCode}`;
      let legacyItems: any[] = [];
      if (selectedCycleYear < 2027) {
        const [profileResponse, productsResponse] = await Promise.all([
          profileClientApi.getByClientCode(clientCode, selectedCycleYear),
          productsApi.getAll(selectedCycleYear),
        ]);
        const products = productsResponse.data || [];
        legacyItems = (profileResponse.data || []).map((profile: any) => {
          const product = products.find((candidate: any) => candidate.id === profile.productId);
          const description = String(product?.descricaoProduto || product?.descricao_produto || "").toLowerCase();
          if (!product || !(description.includes("2º semestre")
            || description.includes("2o semestre")
            || description.includes("segundo semestre"))) return null;
          const quantity = Number(profile.contractualQuantity || 0);
          if (quantity <= 0) return null;
          const price = Number(product.preco || 0);
          const discount = Number(profile.discount || 0);
          const netPrice = Math.round(price * (1 - discount / 100) * 100) / 100;
          return {
            productCode: product.codigoProduto || product.codigo_produto,
            productSku: product.sku,
            productDescription: product.descricaoProduto || product.descricao_produto,
            productSeries: String(product.serie || ""),
            educationLevel: String(product.segmento || ""),
            brand: String(product.marca || ""),
            academicYear: String(product.anoLetivo || product.ano_letivo || ""),
            unitPrice: price,
            contractualDiscount: discount,
            discountAmount: price - netPrice,
            netPrice,
            quantityContracted: quantity,
            quantityOrdered: quantity,
            itemTotal: netPrice * quantity,
          };
        }).filter(Boolean);
      }
      const legacyTotal = legacyItems.reduce((sum, item) => sum + item.itemTotal, 0);
      const legacyQuantity = legacyItems.reduce((sum, item) => sum + item.quantityOrdered, 0);
      const orderData: CreateOrderDto = {
        order_number: orderNumber,
        backofficeReason: null,
        client_name: clientName,
        created_by: user?.email || getUserEmailSync() || "system",
        client_code: clientCode,
        total_amount: legacyTotal,
        shipping_amount: 0,
        subtotal_amount: legacyTotal,
        total_items: legacyQuantity,
        status: "pending_confirmation",
        client_company_name: clientName,
        deliveryTypeId: 2,
        isScheduled: true,
        cycle_code: buildCycleCode(selectedCycleYear, "PROGRAMADO"),
        customer: { clientCodeCnx: clientCode, clientCompanyName: clientName },
        items: legacyItems,
      };

      const result: any = await ordersApi.create(orderData);
      const officialOrderNumber = result?.orderNumber || result?.order_number || orderNumber;
      toast.success(`Pedido programado ${officialOrderNumber} gerado com sucesso!`, { id: toastId });
      return { success: true, orderNumber: officialOrderNumber };
    } catch (error: any) {
      const cycleRequired = isCycleRequiredError(error);
      if (cycleRequired) {
        toast.error(
          `Ciclo comercial ambíguo para o cliente ${clientCode}. Ciclos disponíveis: ${cycleRequired.availableCycles.join(", ")}.`,
          { id: toastId },
        );
        return { success: false, error: cycleRequired.message };
      }
      const invalidCycleCode = isInvalidCycleCodeFormatError(error);
      if (invalidCycleCode) {
        toast.error(invalidCycleCode.message, { id: toastId });
        return { success: false, error: invalidCycleCode.message };
      }
      if (isCycleNotOpenError(error) || isCycleNotConfiguredError(error)) {
        const message = error?.response?.data?.message || "Ciclo comercial indisponível.";
        toast.error(message, { id: toastId });
        return { success: false, error: message };
      }
      const message = error?.response?.data?.message || error.message;
      toast.error(`Erro ao gerar pedido programado: ${message}`, { id: toastId });
      return { success: false, error: message };
    } finally {
      setIsCreating(false);
      processingClients.delete(clientCode);
    }
  };

  return { createScheduledOrder, isCreating };
};
